/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { Shop, ShopProduct } from '../services/shopService';
// apiService - unused but may be needed later
import { getValidImageUrl } from '../utils/imageUtils';
import './SearchResults.css';

interface SearchResult {
  shop: Shop;
  products: ShopProduct[];
  totalProducts: number;
}

interface SearchResultsProps {
  results: SearchResult[];
  onAddToCart: (product: ShopProduct, shop: Shop) => void;
  onViewShop: (shop: Shop) => void;
  loading?: boolean;
  error?: string | null;
  cartItems?: Array<{ product: ShopProduct; shop: Shop; quantity: number }>;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  onAddToCart,
  onViewShop,
  loading = false,
  error = null,
  cartItems = []
}) => {
  const [expandedShops, setExpandedShops] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'relevance' | 'price' | 'rating' | 'name'>('relevance');

  // Check if a product is already in the cart
  const isProductInCart = (product: ShopProduct, shop: Shop): boolean => {
    return cartItems.some(item => 
      item.product.id === product.id && item.shop.id === shop.id
    );
  };

  const getShopTypeIcon = (type: string) => {
    switch (type) {
      case 'medical': return '🏥';
      case 'bakery': return '🥖';
      case 'restaurant': return '🍛';
      case 'grocery': return '🛒';
      case 'electronics': return '📱';
      case 'clothing': return '👕';
      case 'pharmacy': return '💊';
      default: return '🏪';
    }
  };

  const toggleShopExpansion = (shopId: string) => {
    setExpandedShops(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shopId)) {
        newSet.delete(shopId);
      } else {
        newSet.add(shopId);
      }
      return newSet;
    });
  };

  const sortResults = (results: SearchResult[]) => {
    return [...results].sort((a, b) => {
      switch (sortBy) {
        case 'price':
          const aMinPrice = Math.min(...a.products.map(p => p.price));
          const bMinPrice = Math.min(...b.products.map(p => p.price));
          return aMinPrice - bMinPrice;
        case 'rating':
          const aAvgRating = a.products.reduce((sum, p) => sum + p.rating, 0) / a.products.length;
          const bAvgRating = b.products.reduce((sum, p) => sum + p.rating, 0) / b.products.length;
          return bAvgRating - aAvgRating;
        case 'name':
          return a.shop.name.localeCompare(b.shop.name);
        default:
          return b.totalProducts - a.totalProducts; // relevance by product count
      }
    });
  };

  const getProductRating = (rating: number) => {
    return '⭐'.repeat(Math.floor(rating)) + (rating % 1 >= 0.5 ? '⭐' : '');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="search-results">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Searching across all shops...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="search-results">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="search-results">
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h3>No products found</h3>
          <p>Try adjusting your search criteria or browse different categories.</p>
        </div>
      </div>
    );
  }

  const totalProducts = results.reduce((sum, result) => sum + result.totalProducts, 0);
  const sortedResults = sortResults(results);

  return (
    <div className="search-results">
      <div className="results-header">
        <div className="results-summary">
          <h2>Search Results</h2>
          <p>
            Found {totalProducts} products across {results.length} shop{results.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="results-controls">
          <label htmlFor="sort-select">Sort by:</label>
          <select
            id="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="sort-select"
          >
            <option value="relevance">Relevance</option>
            <option value="price">Price (Low to High)</option>
            <option value="rating">Rating (High to Low)</option>
            <option value="name">Shop Name</option>
          </select>
        </div>
      </div>

      <div className="results-list">
        {sortedResults.map((result) => (
          <div key={result.shop.id} className="shop-result">
            <div className="shop-header" onClick={() => toggleShopExpansion(result.shop.id)}>
              <div className="shop-info">
                <div className="shop-icon">
                  {getShopTypeIcon(result.shop.type)}
                </div>
                <div className="shop-details">
                  <h3 className="shop-name">{result.shop.name}</h3>
                  <p className="shop-address">{result.shop.address}</p>
                  <div className="shop-meta">
                    <span className="shop-type">{result.shop.type}</span>
                    <span className="product-count">
                      {result.totalProducts} product{result.totalProducts !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className="shop-actions">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewShop(result.shop);
                  }}
                  className="view-shop-btn"
                >
                  View Shop
                </button>
                <button className="expand-btn">
                  {expandedShops.has(result.shop.id) ? '▼' : '▶'}
                </button>
              </div>
            </div>

            {expandedShops.has(result.shop.id) && (
              <div className="products-section">
                <div className="products-grid">
                  {result.products.map((product) => (
                    <div key={product.id} className="product-card">
                      <div className="product-image">
                        <img 
                          src={getValidImageUrl(product.image)}
                          alt={product.name}
                          onError={(e) => {
                            console.log('🌐 SearchResults - Image failed to load:', product.image);
                            e.currentTarget.src = 'https://via.placeholder.com/150x150/667eea/ffffff?text=No+Image';
                          }}
                          onLoad={() => console.log('🌐 SearchResults - Image loaded successfully:', product.image)}
                        />
                      </div>
                      <div className="product-info">
                        <h4 className="product-name">{product.name}</h4>
                        <p className="product-description">{product.description}</p>
                        <div className="product-meta">
                          <span className="product-brand">{product.brand}</span>
                          <span className="product-category">{product.category}</span>
                        </div>
                        <div className="product-rating">
                          {getProductRating(product.rating)}
                          <span className="rating-text">({product.reviews} reviews)</span>
                        </div>
                        <div className="product-price-section">
                          <span className="product-price">{formatPrice(product.price)}</span>
                          {product.discount > 0 && (
                            <span className="product-discount">
                              {product.discount}% off
                            </span>
                          )}
                        </div>
                        <div className="product-stock">
                          {product.inStock ? (
                            <span className="in-stock">✅ In Stock</span>
                          ) : (
                            <span className="out-of-stock">❌ Out of Stock</span>
                          )}
                        </div>
                        <button
                          onClick={() => onAddToCart(product, result.shop)}
                          disabled={!product.inStock || isProductInCart(product, result.shop)}
                          className={`add-to-cart-btn ${isProductInCart(product, result.shop) ? 'disabled' : ''}`}
                        >
                          {!product.inStock ? 'Out of Stock' : 
                           isProductInCart(product, result.shop) ? 'In Cart' : 'Add to Cart'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchResults;

